import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Briefcase, Plus, Pencil, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Designations() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", department: "Engineering" });

  const load = () => api.get("/designations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", department: "Engineering" }); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name, department: d.department || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/designations/${editing.id}`, form);
      else await api.post("/designations", form);
      toast.success(editing ? "Designation updated" : "Designation added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (d) => {
    try { await api.put(`/designations/${d.id}`, { active: !d.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (d) => {
    try { await api.delete(`/designations/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (items === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Designations" description="Job titles available when adding employees. A designation in use cannot be deleted — deactivate it instead." testid="designations-page">
        <Button onClick={openNew} data-testid="add-designation-button" className="gap-1.5"><Plus className="w-4 h-4" /> New designation</Button>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState icon={Briefcase} title="No designations yet" description="Add your first job title to start creating employees." testid="designations-empty">
          <Button onClick={openNew} className="gap-1.5"><Plus className="w-4 h-4" /> New designation</Button>
        </EmptyState>
      ) : (
        <div className="bg-card border border-border rounded-md divide-y divide-border" data-testid="designations-list">
          {items.map((d) => (
            <div key={d.id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`designation-row-${d.id}`}>
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2">{d.name} {!d.active && <StatusPill active={false} />}</div>
                <div className="text-xs text-muted-foreground">{d.department || "—"}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`edit-designation-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => toggle(d)} title={d.active ? "Deactivate" : "Activate"} data-testid={`toggle-designation-${d.id}`}><Power className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(d)} data-testid={`delete-designation-${d.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="designation-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit designation" : "New designation"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="label-caps">Title</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="designation-name-input" /></div>
            <div><Label className="label-caps">Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1.5" data-testid="designation-department-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name.trim()} data-testid="save-designation-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
